/*
 * The markdown-it instance a dialect reads with. Every option here is a decision about what the
 * dialect *is* — which constructs exist, which destinations survive, whether a bare address becomes
 * a link — so a dialect states them as data and this file turns them into a tokenizer.
 *
 * One instance serves everything that reads: a second one configured differently cuts the same file
 * into different blocks, which is how five Google-Docs exports came out with their link reference
 * definitions in one place and their text in another (S-02 A.1).
 */

import type { Options } from "markdown-it"
import MarkdownIt from "markdown-it"
import type Token from "markdown-it/lib/token.mjs"

/** A rule that runs over the whole token stream once, after markdown-it's own core rules. */
export interface CoreRule {
	name: string
	run: (state: { tokens: Token[] }) => void
}

export interface TokenizerConfig {
	options: Options
	/** markdown-it rules this dialect does not have: `lheading`, indented `code`, `entity`. */
	disable: string[]
	/** Whether a destination may become a link, an image or an anchor target. */
	validateLink: (dest: string) => boolean
	/** What the linkifier catches, and the schemes it is taught; absent where `linkify` is off. */
	linkify?: {
		set: Parameters<MarkdownIt["linkify"]["set"]>[0]
		/** A schema the linkifier learns, with the rule it is recognised by — `null` for "never". */
		add: [string, Parameters<MarkdownIt["linkify"]["add"]>[1]][]
	}
	coreRules?: CoreRule[]
}

/** `- [ ] ` and `- [x] `: the checkbox a task item opens with, and the space that closes it. */
const TASK_MARKER = /^\[([ xX])\](?:[ \t]|$)/

/**
 * The task-list pre-pass. markdown-it has no task-list rule, and a plugin would bring a token type
 * of its own; the checkbox is instead lifted off the item's first line onto the `list_item_open`
 * token, exactly where the ProseMirror attribute wants it, and struck from the text so it is not
 * typed twice.
 */
export const taskListRule: CoreRule = {
	name: "teambase_tasks",
	run: (state) => {
		const tokens = state.tokens
		for (let i = 0; i < tokens.length; i++) {
			const open = tokens[i]
			if (open === undefined || open.type !== "list_item_open") continue
			const inline = tokens[i + 2]
			if (tokens[i + 1]?.type !== "paragraph_open" || inline?.type !== "inline") continue
			const marker = TASK_MARKER.exec(inline.content)
			if (marker === null) continue
			const first = inline.children?.[0]
			if (first === undefined || first.type !== "text" || TASK_MARKER.exec(first.content) === null)
				continue
			open.meta = { checked: marker[1] !== " " }
			inline.content = inline.content.slice(marker[0].length)
			first.content = first.content.slice(marker[0].length)
		}
	},
}

export function buildTokenizer(config: TokenizerConfig): MarkdownIt {
	const md = MarkdownIt(config.options)
	md.disable(config.disable)
	if (config.linkify !== undefined) {
		md.linkify.set(config.linkify.set)
		for (const [schema, rule] of config.linkify.add) md.linkify.add(schema, rule)
	}
	// The destination is stored as the author wrote it: percent-encoding it here would write bytes
	// nobody typed, and the two normalizers are the only place markdown-it would do that.
	md.normalizeLink = (url) => url
	md.normalizeLinkText = (url) => url
	md.validateLink = config.validateLink
	for (const rule of config.coreRules ?? []) md.core.ruler.push(rule.name, rule.run)
	return md
}
